

import { DragIndicator as GripVertical, Close as X } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/components/icons';
import { SecureImage } from '@/components/secure-image';
import { EnrichedEntity } from '@/lib/types';
import { resolvePrimaryImage } from '@/lib/utils/image-identity';


interface FloatingEntityCardProps {
  entity: EnrichedEntity;
  initialPosition: { x: number; y: number };
  onClose: () => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  onEntityClick: (event: React.MouseEvent) => void;
}


export function FloatingEntityCard({
  entity,
  initialPosition,
  onClose,
  onPositionChange,
  onEntityClick,
}: FloatingEntityCardProps) {
  const { t } = useTranslation();
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent text selection
    e.stopPropagation(); // Prevent card click
    if (cardRef.current) {
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newPosition = {
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        };
        setPosition(newPosition);
        onPositionChange(newPosition);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onPositionChange]);

  const displayName = entity.name || entity.title || t('editor.unknown');
  const description = entity.shortDescription || entity.description || t('editor.no_description_available');

  // Extract the profile image using its stable UUID.
  let profileImage: string | undefined = entity.mainImageUrl;
  let profilePath: string | undefined = entity.mainImagePath;
  if (entity.images && entity.images.length > 0) {
    const primaryImage = resolvePrimaryImage(entity);
    profileImage = primaryImage?.profile?.url || primaryImage?.original?.url;
    profilePath = primaryImage?.profile?.path || primaryImage?.original?.path;
  }


  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger if not clicking on close button or drag handle

    if (!isDragging) {
      onEntityClick(e);
    }
  };

  return (
    <Card
      ref={cardRef}
      onClick={handleCardClick}
      sx={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: 280,
        cursor: isDragging ? 'grabbing' : 'pointer',
        zIndex: 1200,
        boxShadow: 4,
        border: 1,
        borderColor: 'primary.main',
        opacity: isDragging ? 0.9 : 1,
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      {/* Header with drag handle, name, and close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          onMouseDown={handleMouseDown}
          onClick={(e) => e.stopPropagation()}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
            userSelect: 'none',
            mr: 1,
          }}
        >
          <GripVertical sx={{ fontSize: 18 }} />
        </Box>

        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </Typography>

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
        >
          <X sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Profile Image - Full Width */}
      {profileImage ? (
        <SecureImage
          src={profileImage}
          path={profilePath}
          alt={displayName}
          sx={{
            height: 180,
            width: '100%',
            objectFit: 'cover',
            objectPosition: 'top',
            bgcolor: 'action.hover',
          }}
        />
      ) : (
        <Box
          sx={{
            height: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            opacity: 0.3,
          }}
        >
          <Icons.characters width={80} height={80} />
        </Box>
      )}

      {/* Description */}
      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.875rem',
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
}
